// ============================================================
// TaskFlow OS - Plugin System
// Modular architecture for extending functionality
// ============================================================

import type { EventType } from '../lib/types';

// ---- Plugin Interface ----

export interface PluginManifest {
  name: string;
  displayName: string;
  version: string;
  author: string;
  description: string;
  hooks: EventType[];
  permissions: string[];
  configSchema?: Record<string, PluginConfigField>;
}

export interface PluginConfigField {
  type: 'string' | 'number' | 'boolean' | 'select';
  label: string;
  default: unknown;
  options?: { label: string; value: unknown }[];
  required?: boolean;
}

export interface PluginContext {
  eventType: EventType;
  payload: Record<string, unknown>;
  userId: string;
  projectId?: string;
  workflowId?: string;
  config: Record<string, unknown>;
}

export interface PluginResult {
  success: boolean;
  data?: unknown;
  error?: string;
  sideEffects?: PluginSideEffect[];
}

export interface PluginSideEffect {
  type: 'notification' | 'update_node' | 'create_event' | 'external_api';
  payload: Record<string, unknown>;
}

export type PluginHandler = (context: PluginContext) => Promise<PluginResult>;

// ---- Plugin Registry ----

class PluginRegistry {
  private plugins: Map<string, {
    manifest: PluginManifest;
    handler: PluginHandler;
    enabled: boolean;
  }> = new Map();

  private hookSubscriptions: Map<EventType, Set<string>> = new Map();

  register(manifest: PluginManifest, handler: PluginHandler): void {
    this.plugins.set(manifest.name, {
      manifest,
      handler,
      enabled: false,
    });

    // Register hook subscriptions
    for (const hook of manifest.hooks) {
      if (!this.hookSubscriptions.has(hook)) {
        this.hookSubscriptions.set(hook, new Set());
      }
      this.hookSubscriptions.get(hook)!.add(manifest.name);
    }

    console.log(`[PluginSystem] Registered: ${manifest.displayName} v${manifest.version}`);
  }

  unregister(name: string): void {
    const plugin = this.plugins.get(name);
    if (!plugin) return;

    // Remove hook subscriptions
    for (const hook of plugin.manifest.hooks) {
      this.hookSubscriptions.get(hook)?.delete(name);
    }

    this.plugins.delete(name);
    console.log(`[PluginSystem] Unregistered: ${name}`);
  }

  enable(name: string): boolean {
    const plugin = this.plugins.get(name);
    if (!plugin) return false;
    plugin.enabled = true;
    console.log(`[PluginSystem] Enabled: ${name}`);
    return true;
  }

  disable(name: string): boolean {
    const plugin = this.plugins.get(name);
    if (!plugin) return false;
    plugin.enabled = false;
    console.log(`[PluginSystem] Disabled: ${name}`);
    return true;
  }

  async emit(eventType: EventType, context: Omit<PluginContext, 'eventType' | 'config'>): Promise<PluginResult[]> {
    const subscribers = this.hookSubscriptions.get(eventType);
    if (!subscribers || subscribers.size === 0) return [];

    const results: PluginResult[] = [];

    for (const pluginName of subscribers) {
      const plugin = this.plugins.get(pluginName);
      if (!plugin || !plugin.enabled) continue;

      try {
        const result = await plugin.handler({
          ...context,
          eventType,
          config: {},
        });
        results.push(result);
      } catch (err) {
        results.push({
          success: false,
          error: `Plugin ${pluginName} failed: ${err}`,
        });
      }
    }

    return results;
  }

  getAll(): Array<{ manifest: PluginManifest; enabled: boolean }> {
    return Array.from(this.plugins.values()).map(({ manifest, enabled }) => ({
      manifest,
      enabled,
    }));
  }

  get(name: string): { manifest: PluginManifest; enabled: boolean } | undefined {
    const plugin = this.plugins.get(name);
    if (!plugin) return undefined;
    return { manifest: plugin.manifest, enabled: plugin.enabled };
  }
}

// ---- Singleton ----

export const pluginRegistry = new PluginRegistry();

// ---- Built-in Plugins ----

// Notification Plugin
pluginRegistry.register(
  {
    name: 'core-notifications',
    displayName: '通知システム',
    version: '1.0.0',
    author: 'TaskFlow',
    description: 'ノードの状態変更時にリアルタイム通知を送信します',
    hooks: ['node_status_changed', 'approval_requested', 'comment_added'],
    permissions: ['notifications.create'],
  },
  async (ctx) => {
    console.log(`[Notification] Event: ${ctx.eventType}`, ctx.payload);
    return {
      success: true,
      sideEffects: [
        {
          type: 'notification',
          payload: {
            title: `${ctx.eventType} が発生しました`,
            content: JSON.stringify(ctx.payload),
          },
        },
      ],
    };
  }
);

// Analytics Plugin
pluginRegistry.register(
  {
    name: 'core-analytics',
    displayName: '分析ダッシュボード',
    version: '1.0.0',
    author: 'TaskFlow',
    description: 'プロジェクトの進捗率・統計データを収集・表示します',
    hooks: ['node_status_changed', 'workflow_updated'],
    permissions: ['analytics.read'],
  },
  async (ctx) => {
    console.log(`[Analytics] Tracking: ${ctx.eventType}`, ctx.payload);
    return { success: true, data: { tracked: true } };
  }
);

// Enable core plugins by default
pluginRegistry.enable('core-notifications');
pluginRegistry.enable('core-analytics');
